import { Card } from "antd";
import {
    AppstoreOutlined,
    BarChartOutlined,
    ClockCircleOutlined,
    CommentOutlined,
    EnvironmentOutlined,
    FontSizeOutlined,
    GlobalOutlined,
    StarOutlined,
} from "@ant-design/icons";
import { CSSProperties, ReactNode } from "react";
import { EmptyStateBox } from "../../../../components/design/EmptyStateBox/EmptyStateBox";

export const AiReportBlockTitle = ({ children }: { children: ReactNode }) => (
    <div style={{
        fontSize: 13,
        fontWeight: 700,
        color: '#5b21b6',
        lineHeight: 1.3,
        marginBottom: 10,
    }}>
        {children}
    </div>
);

type PositionDiffFactor = {
    factorId: string;
    label: string;
    correlation: number;
    relativeWeight: number;
    sampleSize: number;
    averageValue?: number | null;
};

const BOOLEAN_FACTORS = new Set([
    'hasWebsite',
    'nameContainsKeyword',
    'categoryContainsKeyword',
]);

const FACTOR_ROW_LABELS: Record<string, string> = {
    rating: 'Puntuación',
    userRatingsTotal: 'Nº reseñas',
    distanceToPoint: 'Distancia',
    openNow: '% de abiertos',
    hasWebsite: 'Tiene web vinculada',
    nameContainsKeyword: 'Keyword en el nombre',
    categoryContainsKeyword: 'Keyword en la categoría',
};

const FACTOR_ICONS: Record<string, ReactNode> = {
    distanceToPoint: <EnvironmentOutlined />,
    rating: <StarOutlined />,
    userRatingsTotal: <CommentOutlined />,
    openNow: <ClockCircleOutlined />,
    hasWebsite: <GlobalOutlined />,
    nameContainsKeyword: <FontSizeOutlined />,
    categoryContainsKeyword: <AppstoreOutlined />,
};

const FactorIconBadge = ({ factorId }: { factorId: string }) => {
    const icon = FACTOR_ICONS[factorId];
    if (!icon) return null;

    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: 6,
            background: '#f5f3ff',
            color: '#7c3aed',
            fontSize: 12,
            flexShrink: 0,
        }}>
            {icon}
        </span>
    );
};

const formatAverage = (factor: PositionDiffFactor) => {
    const value = factor.averageValue;
    if (value == null || Number.isNaN(value)) return null;

    if (BOOLEAN_FACTORS.has(factor.factorId)) {
        return `${Math.round(100 * value)}%`;
    }

    if (factor.factorId === 'openNow') {
        // averageValue viene como ratio 0–1 (p.ej. 0.9918 → 99%)
        return `${Math.round(100 * value)}%`;
    }

    if (factor.factorId === 'rating') {
        return (Math.round(10 * value) / 10).toFixed(1);
    }

    if (factor.factorId === 'userRatingsTotal') {
        return Math.round(value).toLocaleString('es-ES');
    }

    if (factor.factorId === 'distanceToPoint') {
        return `${Math.round(value)} mts.`;
    }

    return (Math.round(100 * value) / 100).toString();
};

const averageHint = (factor: PositionDiffFactor) => {
    if (BOOLEAN_FACTORS.has(factor.factorId)) {
        if (factor.factorId === 'hasWebsite') return 'con web';
        return 'con keyword';
    }
    return 'media';
};

const WEIGHT_SEGMENT_COUNT = 12;

const RelativeWeightMeter = ({ percent }: { percent: number }) => {
    const clamped = Math.max(0, Math.min(100, percent));
    const filledSegments = Math.round((clamped / 100) * WEIGHT_SEGMENT_COUNT);

    return (
        <div
            role="img"
            aria-label={`Peso ${clamped}%`}
            style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${WEIGHT_SEGMENT_COUNT}, 1fr)`,
                gap: 3,
                alignItems: 'end',
                height: 22,
            }}
        >
            {Array.from({ length: WEIGHT_SEGMENT_COUNT }).map((_, index) => {
                const isFilled = index < filledSegments;
                const height = 8 + Math.round((index / (WEIGHT_SEGMENT_COUNT - 1)) * 14);
                return (
                    <span
                        key={index}
                        style={{
                            display: 'block',
                            height: isFilled ? height : 6,
                            borderRadius: 2,
                            background: isFilled
                                ? `color-mix(in srgb, #475569 ${50 + index * 3}%, #94a3b8)`
                                : '#f1f5f9',
                            border: isFilled ? 'none' : '1px solid #e2e8f0',
                            boxSizing: 'border-box',
                            transition: 'height 0.15s ease',
                        }}
                    />
                );
            })}
        </div>
    );
};

const RelativeWeightFactorCard = ({ factor, wide }: { factor: PositionDiffFactor, wide?: boolean }) => {
    const weightPct = Math.round(1000 * (factor.relativeWeight ?? 0)) / 10;
    const rowLabel = FACTOR_ROW_LABELS[factor.factorId] || factor.label;

    return (
        <div style={{
            padding: '11px 12px 10px',
            borderRadius: 10,
            background: 'linear-gradient(165deg, #faf5ff 0%, #ffffff 55%)',
            border: '1px solid #ddd6fe',
            boxShadow: '0 1px 2px rgba(91, 33, 182, 0.06)',
            minHeight: '100%',
            boxSizing: 'border-box',
            gridColumn: wide ? '1 / -1' : undefined,
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 10,
            }}>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    minWidth: 0,
                }}>
                    <FactorIconBadge factorId={factor.factorId} />
                    <span style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#64748b',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        lineHeight: 1.25,
                    }}>
                        {rowLabel}
                    </span>
                </span>
                <span style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: '#475569',
                    lineHeight: 1,
                    flexShrink: 0,
                }}>
                    {weightPct}%
                </span>
            </div>
            <RelativeWeightMeter percent={weightPct} />
        </div>
    );
};

const AVERAGE_LAYOUT_IDS = [
    'distanceToPoint',
    'rating',
    'userRatingsTotal',
    'openNow',
    'hasWebsite',
    'nameContainsKeyword',
    'categoryContainsKeyword',
] as const;

const placeholderFactor = (factorId: string): PositionDiffFactor => ({
    factorId,
    label: FACTOR_ROW_LABELS[factorId] || factorId,
    correlation: 0,
    relativeWeight: 0,
    sampleSize: 0,
    averageValue: null,
});

const AverageFactorCard = ({ factor, wide }: { factor: PositionDiffFactor, wide?: boolean }) => {
    const avgLabel = formatAverage(factor);
    const rowLabel = FACTOR_ROW_LABELS[factor.factorId] || factor.label;
    const hasValue = avgLabel != null;

    return (
        <div style={{
            padding: '12px 14px',
            borderRadius: 10,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            minHeight: '100%',
            boxSizing: 'border-box',
            gridColumn: wide ? '1 / -1' : undefined,
        }}>
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                marginBottom: 8,
                minWidth: 0,
            }}>
                <FactorIconBadge factorId={factor.factorId} />
                <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#94a3b8',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    lineHeight: 1.25,
                }}>
                    {rowLabel}
                </span>
            </div>
            <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                minHeight: 28,
            }}>
                <span style={{
                    fontSize: wide ? 22 : 18,
                    fontWeight: 800,
                    color: hasValue ? '#0f172a' : '#cbd5e1',
                    lineHeight: 1.1,
                }}>
                    {avgLabel ?? '—'}
                </span>
                {BOOLEAN_FACTORS.has(factor.factorId) && hasValue && (
                    <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 11 }}>
                        {averageHint(factor)}
                    </span>
                )}
            </div>
        </div>
    );
};

export const PositionDiffFactorsDashboard = (props: { factors?: PositionDiffFactor[] | null }) => {
    const factors = props.factors || [];
    const byId = Object.fromEntries(factors.map((f) => [f.factorId, f]));

    const weightSubtitleStyle: CSSProperties = {
        fontSize: 10,
        color: '#94a3b8',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        marginBottom: 8,
    };

    const geoWeightFactors = factors.filter((f) => f.factorId === 'distanceToPoint');

    const businessWeightFactors = factors
        .filter((f) => f.factorId !== 'distanceToPoint')
        .sort((a, b) => (b.relativeWeight ?? 0) - (a.relativeWeight ?? 0));

    // Fixed medias layout: always same slots, same order
    const geoAverageFactors = AVERAGE_LAYOUT_IDS
        .filter((id) => id === 'distanceToPoint')
        .map((id) => byId[id] || placeholderFactor(id));

    const businessAverageFactors = AVERAGE_LAYOUT_IDS
        .filter((id) => id !== 'distanceToPoint')
        .map((id) => byId[id] || placeholderFactor(id));

    if (!factors.length) {
        return (
            <div style={{
                height: '100%',
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                overflowY: 'auto',
            }}>
                <EmptyStateBox
                    icon={<BarChartOutlined />}
                    title="No hay análisis de factores en este lanzamiento"
                    description="Lanza un nuevo estudio si quieres tener la correlación de los diferentes factores"
                />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Card
                size="small"
                style={{ borderRadius: 10, border: '1px solid #e9d5ff', background: 'rgba(255,255,255,0.85)', flexShrink: 0 }}
                bodyStyle={{ padding: '12px 14px' }}
            >
                <AiReportBlockTitle>Correlación de factores SEO Local</AiReportBlockTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {geoWeightFactors.length > 0 && (
                        <div>
                            <div style={weightSubtitleStyle}>Geolocalización</div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gap: 10,
                            }}>
                                {geoWeightFactors.map((factor) => (
                                    <RelativeWeightFactorCard
                                        key={`w-${factor.factorId}`}
                                        factor={factor}
                                        wide
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {businessWeightFactors.length > 0 && (
                        <div>
                            <div style={weightSubtitleStyle}>Negocio</div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gap: 10,
                            }}>
                                {businessWeightFactors.map((factor) => (
                                    <RelativeWeightFactorCard
                                        key={`w-${factor.factorId}`}
                                        factor={factor}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            <Card
                size="small"
                style={{ borderRadius: 10, border: '1px solid #e9d5ff', background: 'rgba(255,255,255,0.85)', flexShrink: 0 }}
                bodyStyle={{ padding: '12px 14px' }}
            >
                <AiReportBlockTitle>Medias del ranking del TOP 10</AiReportBlockTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                        <div style={weightSubtitleStyle}>Geolocalización</div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 10,
                        }}>
                            {geoAverageFactors.map((factor) => (
                                <AverageFactorCard
                                    key={`avg-${factor.factorId}`}
                                    factor={factor}
                                    wide
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <div style={weightSubtitleStyle}>Negocio</div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 10,
                        }}>
                            {businessAverageFactors.map((factor) => (
                                <AverageFactorCard
                                    key={`avg-${factor.factorId}`}
                                    factor={factor}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};
